const COOKIE_NAME = "sr_meta_attribution";
const MAX_VALUE_LENGTH = 160;
const MAX_LANDING_PAGE_LENGTH = 500;

export type MetaAttribution = {
  session_id: string;
  landing_page: string;
  captured_at: string;
  fbclid?: string;
  fbc?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

const sanitize = (value: string | null | undefined, maxLength = MAX_VALUE_LENGTH) => {
  if (!value) return undefined;
  const normalized = value.trim().replace(/[\u0000-\u001f\u007f]/g, "");
  return normalized ? normalized.slice(0, maxLength) : undefined;
};

const getCookie = (name: string) => {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  const raw = document.cookie.split(";").map((value) => value.trim()).find((value) => value.startsWith(prefix));
  return raw ? decodeURIComponent(raw.slice(prefix.length)) : undefined;
};

const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=7776000; Path=/; SameSite=Lax; Secure`;
};

export const buildFbc = (fbclid: string, timestamp = Date.now()) => {
  const value = sanitize(fbclid);
  return value ? `fb.1.${timestamp}.${value}` : undefined;
};

const readStoredAttribution = (): MetaAttribution | undefined => {
  const raw = getCookie(COOKIE_NAME);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<MetaAttribution>;
    if (typeof parsed.session_id !== "string" || typeof parsed.landing_page !== "string") return undefined;
    return {
      session_id: parsed.session_id.slice(0, MAX_VALUE_LENGTH),
      landing_page: parsed.landing_page.slice(0, MAX_LANDING_PAGE_LENGTH),
      captured_at: typeof parsed.captured_at === "string" ? parsed.captured_at : new Date().toISOString(),
      ...Object.fromEntries(
        ["fbclid", "fbc", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].flatMap(
          (key) => {
            const value = sanitize(parsed[key as keyof MetaAttribution] as string | undefined);
            return value ? [[key, value]] : [];
          }
        )
      ),
    } as MetaAttribution;
  } catch {
    return undefined;
  }
};

export const captureMetaAttribution = () => {
  if (typeof window === "undefined") return undefined;

  const existing = readStoredAttribution();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const fbclid = sanitize(params.get("fbclid"));
  const attribution: MetaAttribution = {
    session_id: crypto.randomUUID(),
    landing_page: window.location.href.slice(0, MAX_LANDING_PAGE_LENGTH),
    captured_at: new Date().toISOString(),
    ...(fbclid && { fbclid, fbc: buildFbc(fbclid) }),
    ...Object.fromEntries(
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].flatMap((key) => {
        const value = sanitize(params.get(key));
        return value ? [[key, value]] : [];
      })
    ),
  };

  setCookie(COOKIE_NAME, JSON.stringify(attribution));
  return attribution;
};

export const getMetaAttributionForBooking = () => {
  const stored = captureMetaAttribution();
  if (!stored) return undefined;

  const fbp = sanitize(getCookie("_fbp"));
  const fbc = sanitize(getCookie("_fbc")) || stored.fbc;
  return {
    ...stored,
    ...(fbp && { fbp }),
    ...(fbc && { fbc }),
  };
};
